import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, Subscription, debounceTime, distinctUntilChanged, of, switchMap } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

import { AuthService } from '../../services/auth.service';
import {
  GlobalSearchService,
  SearchGroup,
  SearchHit,
} from '../../services/global-search.service';
import { filterNavPages } from '../nav/nav-pages';

@Component({
  selector: 'app-global-search-palette',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, TranslocoModule],
  templateUrl: './global-search-palette.component.html',
  styleUrls: ['./global-search-palette.component.css'],
})
export class GlobalSearchPaletteComponent implements OnDestroy {
  @ViewChild('queryInput') queryInput?: ElementRef<HTMLInputElement>;

  private readonly searchApi = inject(GlobalSearchService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly i18n = inject(TranslocoService);

  readonly open = this.searchApi.open;
  readonly query = signal('');
  readonly loading = signal(false);
  readonly apiGroups = signal<SearchGroup[]>([]);
  readonly activeIndex = signal(0);
  readonly isMac = signal(
    typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform),
  );

  private readonly query$ = new Subject<string>();
  private sub?: Subscription;

  readonly pageHits = computed<SearchHit[]>(() => {
    const q = this.query().trim();
    return filterNavPages(q, (r) => this.auth.hasRole(r)).map((e) => ({
      kind: (e.kind || 'page') as SearchHit['kind'],
      id: e.id,
      title: e.title,
      subtitle: e.subtitle || e.route,
      route: e.route,
      icon: e.icon,
    }));
  });

  readonly recentHits = computed<SearchHit[]>(() => {
    if (this.query().trim()) return [];
    return this.searchApi.getRecent().map((h) => ({ ...h, kind: 'recent' as const }));
  });

  readonly flatHits = computed<SearchHit[]>(() => {
    const out: SearchHit[] = [];
    const pages = this.pageHits().filter((h) => h.kind === 'page');
    const actions = this.pageHits().filter((h) => h.kind === 'action');
    if (this.recentHits().length) out.push(...this.recentHits());
    out.push(...pages);
    for (const g of this.apiGroups()) out.push(...(g.items || []));
    out.push(...actions);
    return out;
  });

  readonly displayGroups = computed(() => {
    const groups: { kind: string; label: string; items: SearchHit[] }[] = [];
    if (this.recentHits().length) {
      groups.push({
        kind: 'recent',
        label: this.i18n.translate('navbar.search_recent') || 'Récents',
        items: this.recentHits(),
      });
    }
    const pages = this.pageHits().filter((h) => h.kind === 'page');
    if (pages.length) {
      groups.push({
        kind: 'page',
        label: this.i18n.translate('navbar.search_pages') || 'Pages',
        items: pages,
      });
    }
    for (const g of this.apiGroups()) {
      if (g.items?.length) groups.push(g);
    }
    const actions = this.pageHits().filter((h) => h.kind === 'action');
    if (actions.length) {
      groups.push({
        kind: 'action',
        label: this.i18n.translate('navbar.search_actions') || 'Actions',
        items: actions,
      });
    }
    return groups;
  });

  constructor() {
    this.sub = this.query$
      .pipe(
        debounceTime(220),
        distinctUntilChanged(),
        switchMap((q) => {
          const trimmed = q.trim();
          if (trimmed.length < 2) {
            this.apiGroups.set([]);
            this.loading.set(false);
            return of(null);
          }
          this.loading.set(true);
          return this.searchApi.search(trimmed).pipe(
            finalize(() => this.loading.set(false)),
          );
        }),
      )
      .subscribe({
        next: (res) => {
          if (!res) return;
          this.apiGroups.set(res.groups || []);
          this.activeIndex.set(0);
        },
        error: () => {
          this.loading.set(false);
          this.apiGroups.set([]);
        },
      });

    effect(() => {
      if (this.open()) {
        this.query.set('');
        this.apiGroups.set([]);
        this.activeIndex.set(0);
        queueMicrotask(() => this.queryInput?.nativeElement?.focus());
      }
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  @HostListener('document:keydown', ['$event'])
  onGlobalKey(ev: KeyboardEvent): void {
    const key = ev.key?.toLowerCase();
    if ((ev.metaKey || ev.ctrlKey) && key === 'k') {
      // Ne pas voler Ctrl+K dans un champ textarea/code éditeur riche
      const t = ev.target as HTMLElement | null;
      const tag = (t?.tagName || '').toLowerCase();
      const editing =
        tag === 'textarea' ||
        (tag === 'input' && (t as HTMLInputElement).type === 'text' && t?.closest('.chatter-composer'));
      if (editing && this.open()) {
        /* allow close */
      }
      ev.preventDefault();
      this.searchApi.toggle();
      return;
    }
    if (!this.open()) return;
    if (key === 'escape') {
      ev.preventDefault();
      this.close();
    }
  }

  onQueryInput(value: string): void {
    this.query.set(value);
    this.activeIndex.set(0);
    this.query$.next(value);
  }

  onKeydown(ev: KeyboardEvent): void {
    const hits = this.flatHits();
    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      if (!hits.length) return;
      this.activeIndex.update((i) => (i + 1) % hits.length);
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      if (!hits.length) return;
      this.activeIndex.update((i) => (i - 1 + hits.length) % hits.length);
    } else if (ev.key === 'Enter') {
      ev.preventDefault();
      const hit = hits[this.activeIndex()];
      if (hit) this.select(hit);
    }
  }

  flatIndexOf(hit: SearchHit): number {
    return this.flatHits().findIndex(
      (h) => h.kind === hit.kind && String(h.id) === String(hit.id) && h.route === hit.route,
    );
  }

  select(hit: SearchHit): void {
    this.searchApi.pushRecent(hit);
    this.close();
    const route = hit.route || '/';
    const [path, qs] = route.split('?');
    const queryParams: Record<string, string> = {};
    if (qs) {
      for (const part of qs.split('&')) {
        const [k, v] = part.split('=');
        if (k) queryParams[decodeURIComponent(k)] = decodeURIComponent(v || '');
      }
    }
    this.router.navigate([path], { queryParams: Object.keys(queryParams).length ? queryParams : undefined });
  }

  close(): void {
    this.searchApi.hide();
  }

  kindLabel(kind: string): string {
    const map: Record<string, string> = {
      page: 'Page',
      action: 'Action',
      customer: 'Client',
      product: 'Produit',
      order: 'Commande',
      kimbyala: 'Kimbyala',
      ticket: 'Ticket',
      recent: 'Récent',
    };
    return map[kind] || kind;
  }

  trackHit = (_: number, h: SearchHit) => `${h.kind}:${h.id}:${h.route}`;
}
